import React from 'react';
import { motion } from 'framer-motion';
import { GithubIcon, LinkedinIcon, MailIcon, CodeIcon, TerminalIcon, CoffeeIcon } from 'lucide-react';
const PROFILE_IMG = 'https://cdn.magicpatterns.com/uploads/b3GBjBJLt8GJFX3HyzZQyh/IMG_8071.jpg';
export const AboutApp: React.FC = () => {
  return <div className="h-full w-full bg-[#1e1e1e] text-white overflow-y-auto mac-scrollbar flex flex-col" data-id="element-5" data-mp="12i1874i9zrsp">
      {/* Hero banner */}
      <div className="relative h-48 bg-[#1d1d4f] flex-shrink-0 overflow-hidden" data-id="element-6" data-mp="17gch9d0qxy9v">
        {/* Soft layered wallpaper-ish backdrop (no UI gradients per guidelines, this is decorative) */}
        <div className="absolute inset-0 opacity-80" style={{
        background: 'radial-gradient(circle at 20% 30%, #4f46e5 0%, transparent 50%), radial-gradient(circle at 80% 70%, #a855f7 0%, transparent 50%), #0f172a'
      }} data-id="element-7" data-mp="1giu4nus50wyg" />
        <div className="absolute inset-0 bg-black/20" data-id="element-8" data-mp="3n91qfg8iijbz" />

        <div className="absolute -bottom-16 left-8 flex items-end gap-6" data-id="element-9" data-mp="3n9tk2liju6zm">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} className="w-32 h-32 rounded-full border-4 border-[#1e1e1e] bg-gradient-to-br from-gray-800 to-black shadow-xl overflow-hidden relative" data-id="element-10" data-mp="3d2hwqiof6e9e">
            <img src={PROFILE_IMG} alt="Emmanuel Saka" className="w-full h-full object-cover" data-id="element-11" data-mp="3w0knb1wm8j9b" />
            <div className="absolute inset-0 ring-2 ring-inset ring-white/10 rounded-full pointer-events-none" data-id="element-12" data-mp="3rz901317sa4a" />
          </motion.div>
          <div className="mb-4" data-id="element-13" data-mp="3i0jnzqw01jvy">
            <h1 className="text-3xl font-bold text-white drop-shadow-md" data-id="element-14" data-mp="3hzi1mq9j9ayx">
              Emmanuel Saka
            </h1>
            <p className="text-blue-200 font-medium drop-shadow-md flex items-center gap-2" data-id="element-15" data-mp="2blirnk32azou">
              Full Stack Developer
              <span className="px-2 py-0.5 rounded-full bg-white/20 text-[10px] backdrop-blur-sm border border-white/10" data-id="element-16" data-mp="282a7m6j70wlm">
                M5 Powered
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-8 pt-24 pb-8 space-y-8" data-id="element-17" data-mp="1z0k7mcb8wpc8">
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.1
      }} className="max-w-2xl text-gray-300 leading-relaxed" data-id="element-18" data-mp="240olrvool7za">
          <p data-id="element-19" data-mp="1gbdvj84yivze">
            Hello! I'm a passionate developer focused on crafting beautiful,
            high-performance web applications. I love bridging the gap between
            design and engineering, creating experiences that feel native and
            polished.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="grid grid-cols-3 gap-4 max-w-2xl" data-id="element-20" data-mp="1bazj7dwbozvk">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center gap-2 hover:-translate-y-1 transition-transform duration-200" data-id="element-21" data-mp="11pgfjir12q2x">
            <CodeIcon className="text-blue-400" size={24} data-id="element-22" data-mp="1aolcikmxybcz" />
            <span className="text-2xl font-bold" data-id="element-23" data-mp="1an0iqx8gawg6">3+</span>
            <span className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-24" data-mp="15oz3yoi25jai">
              Years Coding
            </span>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center gap-2 hover:-translate-y-1 transition-transform duration-200" data-id="element-25" data-mp="1kgzglk1hru2b">
            <TerminalIcon className="text-purple-400" size={24} data-id="element-26" data-mp="1tg4jt3o00bkt" />
            <span className="text-2xl font-bold" data-id="element-27" data-mp="1pesoqimcuy58">20+</span>
            <span className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-28" data-mp="1pc6hyl7agmo2">
              Projects Shipped
            </span>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex flex-col items-center justify-center gap-2 hover:-translate-y-1 transition-transform duration-200" data-id="element-29" data-mp="3nv1m9gm68kmf">
            <CoffeeIcon className="text-amber-400" size={24} data-id="element-30" data-mp="1gqi141xpwng" />
            <span className="text-2xl font-bold" data-id="element-31" data-mp="1ibbeqcij9jq">∞</span>
            <span className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-32" data-mp="2zyw4ywaec6nx">
              Coffee Cups
            </span>
          </div>
        </motion.div>

        {/* Links */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="flex flex-wrap gap-4" data-id="element-33" data-mp="39vimbb3ikfl7">
          <a href="https://github.com/Dev-emmy-001" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#24292e] hover:bg-[#2f363d] rounded-lg transition-all hover:-translate-y-0.5 border border-white/10" data-id="element-34" data-mp="t74s0p6tuotu">
            <GithubIcon size={18} data-id="element-35" data-mp="24wvirz2mj6j5" /> GitHub
          </a>
          <a href="https://www.linkedin.com/in/saka-emmanuel-01b43923b" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#0077b5] hover:bg-[#0088cc] rounded-lg transition-all hover:-translate-y-0.5 border border-white/10" data-id="element-36" data-mp="24vkbvqdvtint">
            <LinkedinIcon size={18} data-id="element-37" data-mp="1q1z4p584voy5" /> LinkedIn
          </a>
          <a href="mailto:emmanuelsaka333@gmail.com" className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all hover:-translate-y-0.5 border border-white/10" data-id="element-38" data-mp="1lzarklcdzvvd">
            <MailIcon size={18} data-id="element-39" data-mp="3ga7okfhy1z7y" /> Email Me
          </a>
        </motion.div>
      </div>
    </div>;
};